import type { Article } from "./types";

export const decisionLog: Article = {
  slug: "decision-log",
  kind: "guide",
  title: "What Is a Decision Log? A Practical Guide for Scaling Teams",
  description:
    "A decision log is a structured record of what your company decided, by whom, on what reasoning, with what outcome. Why notes and spreadsheets stop working, and what a real one needs.",
  lede: "A decision log is a structured record of the decisions a company makes: what was decided, who decided it, the reasoning behind it, and whether the outcome matched the intent. Done well, it is the difference between a company that learns from its decisions and one that keeps relitigating them.",
  datePublished: "2026-06-26",
  sections: [
    {
      type: "prose",
      heading: "What a Decision Log Is",
      body: "A decision log is a single place where major decisions are written down in a consistent shape. Not meeting minutes, and not a task list. A record of the choice itself: the question, the options weighed, the reasoning, the person accountable, and what happened after.\n\nThe point is not bureaucracy. It is continuity. A decision made today shapes decisions made six months from now by people who were not in the room. Without a record, they inherit the outcome but not the logic, and they either rebuild it or quietly contradict it. A decision log carries the reasoning forward so the next call starts where the last one left off.",
    },
    {
      type: "list",
      heading: "What Belongs in a Good Decision Log",
      items: [
        "The decision: the specific choice that was made, stated plainly.",
        "The owner: the named person accountable for the call and its outcome.",
        "Participants: who weighed in, and who was consulted but did not.",
        "The reasoning: why this option over the alternatives, and what was traded off.",
        "The data: the facts and numbers the decision rested on, with their source and date.",
        "The outcome: what actually happened, checked against what was expected.",
      ],
    },
    {
      type: "prose",
      heading: "Why Notes and Spreadsheets Stop Working",
      body: "Most teams start with meeting notes or a spreadsheet. Both can capture that a decision happened. Neither carries the parts that make a decision reusable, and both decay the moment the context that explains them leaves the company.",
    },
    {
      type: "table",
      columns: ["Capability", "Meeting notes", "Spreadsheet", "Decision log"],
      rows: [
        ["Captures the decision", "Sometimes", "Yes", "Yes"],
        ["Captures the reasoning", "Rarely", "Cramped", "Yes"],
        ["Names an accountable owner", "No", "Manual", "Yes"],
        ["Links to the data behind it", "No", "Manual", "Yes"],
        ["Tracks the outcome against intent", "No", "Manual", "Yes"],
      ],
    },
    {
      type: "prose",
      heading: "How a Decision Log Compounds",
      body: "A single decision log entry is useful. A library of them is infrastructure. Once decisions are written in a consistent shape, the record becomes ground truth: for new hires getting up to speed, for leaders auditing how a call was made, and increasingly for the AI agents a company points at its own data. An agent grounded in a real decision record can tell you what was decided and why. One grounded in scattered documents can only guess.\n\nThis is also what closes the loop. When the outcome of a decision is recorded next to its intent, patterns become visible: which kinds of calls go well, which keep going wrong, and where the reasoning was thin. That feedback is impossible when decisions evaporate into chat threads.",
    },
    {
      type: "prose",
      heading: "How Orgtools Approaches the Decision Log",
      body: "Orgtools keeps the decision log as the work happens, not after it. When people and agents do a week's work in a day, a log written up at the weekly sync is already behind. Orgtools finds the decisions a plan depends on, shows who decided what wherever they decided it, including in an agent session, and runs the calls that need a leader in a **Decision Room** that keeps the reasoning, the participants, and the data in one record, with a named person deciding. The fact base is pulled from the tools where work already happens, so the data behind a decision is real and dated rather than retyped. After the call, Orgtools tracks the outcome against the intent and watches for drift, drag, and decay across the record. When a fact underneath a past decision changes, the decision flags itself as stale instead of waiting for someone to notice.",
    },
    {
      type: "cta",
      heading: "Start Your Decision Log",
      href: "/platform",
      label: "Explore the Platform",
    },
  ],
  faq: [
    {
      question: "How is a decision log different from meeting notes?",
      answer:
        "Meeting notes record what was discussed. A decision log records what was decided, and the reasoning, owner, and data behind it. Notes are a transcript. A decision log is a structured record built to be reused by whoever decides next.",
    },
    {
      question: "What fields belong in a decision log?",
      answer:
        "At minimum: the decision, the accountable owner, who participated, the reasoning and tradeoffs, the data it rested on, and the outcome measured against the intent. The outcome field is the one most teams skip, and the one that makes the log worth keeping.",
    },
    {
      question: "Do we need a tool, or is a spreadsheet enough?",
      answer:
        "A spreadsheet works until it does not. It captures that a decision happened but makes the reasoning cramped, the data unlinked, and the outcome a manual chore no one does. A dedicated system keeps the reasoning, the data, and the outcome together and current. Start with a spreadsheet if you must, but expect to outgrow it.",
    },
    {
      question: "How does a decision log help AI agents?",
      answer:
        "AI agents are only as good as what they reason from. Pointed at scattered chats and documents, an agent re-derives an answer every time and holds no memory of what your company actually decided. Pointed at a structured decision record, it can ground its answers in real decisions, reasoning, and outcomes. The decision log is the substrate that makes an agent useful on your real choices.",
    },
  ],
};
